import classNames from 'classnames'
import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Spinner } from 'reactstrap'
import { ReactComponent as ArrowRight } from '../../assets/icons/arrow-right.svg'
import { ReactComponent as Menu } from '../../assets/icons/menu.svg'
import { isBackofficeEnv } from '../../helpers/isBackoffceEnv'
import useFontSelector from '../../hooks/useFontSelector'
import useUploadFile from '../../hooks/useUploadFile'
import { getEnvironment, hasRole } from '../../services/AuthServices'
import { getCurrentStore } from '../../services/CurrentStoreServices'
import { getProductCatalogById, getWebsiteProductCatalogById } from '../../services/ProductLinesServices'
import { getLocalData, setLocalData } from '../../services/StorageServices'
import { toaster } from '../../shared/PFToast'
import NavBar from './NavBar'
import Toolbar from './Toolbar'
import MockupBottom from './artwork-editor/MockupBottom'
import MockupEditor from './artwork-editor/MockupEditor'
import PreviewLiveArtworkContainer from './artwork-editor/preview-live/PreviewLiveArtworkContainer'
import PreviewArtworkContainer from './artwork-editor/preview/PreviewArtworkContainer'
import { useSelectedVariantContext } from './context/SelectedVariantContext'
import {
    actionChangeTabDesign,
    actionInitLibraries,
    actionInitProduct,
    actionSetIsAdmin,
    openUserGuidePopup,
    updateFonts,
    actionSetMappingProduct,
    actionChangeDesignMode,
    updateActiveSide,
} from './context/SelectedVariantContext/action'
import ProductVariantPricingContainer from './product-variant-pricing/ProductVariantPricingContainer'
import UploadContainer from './upload/UploadContainer'
import CampaignDesignLoading from './upload/tabs/campaign-design/CampaignDesignLoading'
import { getMerchizeLibrary } from '../../services/MerchizeLibraryServices'
import Tour from '../../shared/tour'
import { stepsGuide } from '../../constant/guide-steps'
import { hasUserCompletedGuide, markUserCompletedGuide, resolveCanvasFlexible } from '../../helpers/tour'
import { useEditorStageRef } from '../../hooks/useEditorStageRef'
import { MockupType } from '../../constant'
import { useEditorBackground } from '../../hooks/useEditorBackground'
import { searchProductVariants } from '../../services/VariantServices'

const DESIGN_MODE_SELECTION_SHOWN_KEY = 'designModeSelectionShown';

export const DESIGN_MODES = {
  COMBINED: 'combined',
  SPLIT: 'split'
}

const Step2Container = ({productId, mappingProductId, variantId, sku,storeId}) => {
    const [showDesignModeTooltip, setShowDesignModeTooltip] = useState(false)
    const {
        state: {tabDesign, display, product, runningImageCompress, imageCompressProgress, productCreatedType, openUserGuidePopup: openUserGuidePopupContext, background, designMode},
        dispatch,
    } = useSelectedVariantContext()
    const [leftWidth, setLeftWidth] = useState(360)
    const separatorRef = useRef(null)
    const containerRef = useRef(null)
    const [minMaxLeftWidth, setMinMaxLeftWidth] = useState({ min: 300, max: 460 })
    const { pending } = useUploadFile()
    const { ratioDefault = 1 } = useEditorBackground()

    const env = getEnvironment()
    const [loading, setLoading] = useState(true)
    const [openTour, setOpenTour] = useState(false)
    const designLibrary = getLocalData('designLibrary') || []
    const mockupData = product?.mockups[0]
    const { fonts } = useFontSelector()

    const stageRef = useEditorStageRef()
    const mockupType = useMemo(
        () => (productCreatedType === '' ? MockupType.SPLIT_LAYOUT : productCreatedType),
        [productCreatedType],
    )

    const handleCloseTour = () => {
        setOpenTour(false)
        markUserCompletedGuide(mockupType)
    }
    const handleMouseDown = (event) => {
        event.preventDefault()
        document.addEventListener('mousemove', handleMouseMove)
        document.addEventListener('mouseup', handleMouseUp)
    }

    const handleMouseMove = (event) => {
        if (!containerRef.current) return
        const containerRect = containerRef.current.getBoundingClientRect()
        const newLeftWidth = event.clientX - containerRect.left

        if (newLeftWidth > minMaxLeftWidth.min && newLeftWidth < minMaxLeftWidth.max) {
            setLeftWidth(newLeftWidth)
        }
    }

    const handleMouseUp = () => {
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
    }

    const onCloseSidebar = () => {
        dispatch(actionChangeTabDesign(''))
    }

    const getSidebar3DWidth = () => {
        if (document.getElementById('left-sidebar')) {
            setLeftWidth(document.getElementById('left-sidebar').offsetWidth)
        }
        // setLeftWidth(document.getElementById('left-sidebar'))
    }

    const getMaxLeftWidth = () => {
        if (window.innerWidth > 1700) {
            setMinMaxLeftWidth({ min: 300, max: 700 })
            setMinMaxLeftWidth({ min: 300, max: 700 })
            return
        } else if (window.innerWidth > 1500) {
            setMinMaxLeftWidth({ min: 228, max: 508 })
            setMinMaxLeftWidth({ min: 228, max: 508 })
            return
        }
        setMinMaxLeftWidth({ min: 228, max: 520 })
        setMinMaxLeftWidth({ min: 228, max: 520 })
    }

    const _fetchLibraries = async () => {
        try {
            const params = {
                display_on_mockup_generator: true,
            }
            const { data, success, message } = await getMerchizeLibrary(params)
            if (!success) {
                toaster({
                    type: 'error',
                    message,
                })
            }
            dispatch(actionInitLibraries(data))
        } catch (error) {
            console.error('Error fetching libraries:', error)
        } finally {
            setLoading(false)
        }
    }

    const _updateFonts = useCallback(() => {
        dispatch(updateFonts(fonts))
    }, [dispatch, fonts])

    const handleFinishTour = () => {
        markUserCompletedGuide(mockupType)
        setOpenTour(false)
        dispatch(openUserGuidePopup())
    }

    useEffect(() => {
        _updateFonts()
    }, [_updateFonts])

    useEffect(() => {
        const _fetchProductCatalog = async () => {
            try {
                let response
                if (isBackofficeEnv()) {
                    const id = storeId || getCurrentStore()
                    response = await getProductCatalogById(productId, id)
                } else {
                    response = await getWebsiteProductCatalogById(productId)
                    if (designLibrary.length > 0) {
                        const now = new Date()
                        const availableDesigns = designLibrary.filter((design) => {
                            const timeDifference = now - new Date(design.created)
                            const hoursDifference = timeDifference / (1000 * 60 * 60)
                            return hoursDifference <= 1
                        })
                        setLocalData('designLibrary', availableDesigns)
                    }
                }
                const { success, data, message } = response
                if (!success) throw new Error(message)
                const isAdmin = hasRole('admin')
                dispatch(actionSetIsAdmin(isAdmin))
                if (mappingProductId && sku) {
                    data.totalVariantsCount = data.variants?.length || 0
                    data.variants = data.variants.filter((v) => v.sku === sku)
                }
                dispatch(actionInitProduct(data))
            } catch (error) {
                toaster({
                    type: 'error',
                    message: error.message,
                })
            } finally {
                setLoading(false)
            }
        }

        _fetchProductCatalog()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [productId, env, dispatch])

    useEffect(() => {
        getSidebar3DWidth()
        window.addEventListener('resize', getSidebar3DWidth)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mockupData])

    useEffect(() => {
        const completed = hasUserCompletedGuide(mockupType)
        setOpenTour(Boolean(!completed))
    }, [mockupType])

    useEffect(() => {
        getMaxLeftWidth()
        window.addEventListener('resize', getMaxLeftWidth)
        // eslint-disable-next-line react-hooks/exhaustive-deps
        _fetchLibraries()
    }, [])

    useEffect(() => {
        if (mockupData) {
            const isMockupTypeV2OrV3 = productCreatedType === 'preview3D' || productCreatedType === ''

            const hasShownBefore = getLocalData(DESIGN_MODE_SELECTION_SHOWN_KEY) || []

            if (display === 'design' && isMockupTypeV2OrV3 && mockupData.preview_v2?.['all-in-one']?.template_url) {
                dispatch(actionChangeDesignMode(DESIGN_MODES.SPLIT))
                if (!hasShownBefore?.length && hasUserCompletedGuide(mockupType) && !openUserGuidePopupContext) {
                    setShowDesignModeTooltip(true)
                    setLocalData(DESIGN_MODE_SELECTION_SHOWN_KEY, [...hasShownBefore, productId])
                }
            }
        }
    }, [mockupData, productId, hasUserCompletedGuide(mockupType), openUserGuidePopupContext])

    useEffect(() => {
        if (!mappingProductId || !variantId) return
        searchProductVariants(mappingProductId, [], { limit: 1000 })
            .then(({ data, success, message }) => {
                const variant = data.variants.find((v) => v._id === variantId)
                if (success && variant) {
                    const display = variant.options?.map((opt) => opt.value).join(' / ') || ''
                    const mappingProduct = {
                        productId: mappingProductId,
                        variantId,
                        sku,
                        variant: {
                            ...variant,
                            display,
                        },
                    }
                    dispatch(actionSetMappingProduct(mappingProduct))
                }
            })
            .catch((error) => {
                console.error('Error fetching variant:', error)
            })
    }, [mappingProductId])

    const className = display === 'design' ? 'd-block' : 'd-none'

    const steps = useMemo(() => {
        if (mockupType !== MockupType.COMBINE_LAYOUT) return stepsGuide[mockupType]

        const adjustedSteps = stepsGuide[mockupType]?.map((step) => {
            if (step.target === '#test-canvas') {
                if (stageRef) {
                    const sideInfo = product?.mockups[0].preview['Front']
                    const ratio = Math.min(stageRef?.scaleX(), stageRef?.scaleY())
                    const scaleFactor = 1.4
                    let widthHole = sideInfo?.image_width || 1000
                    let heightHole = sideInfo?.image_height || 1000
                    if (sideInfo?.image_width > 6000) {
                        const newScaleX = stageRef.scaleX() / scaleFactor
                        const newScaleY = stageRef.scaleY() / scaleFactor

                        const centerWidth = stageRef.width() / 2
                        const centerHeight = stageRef.height() / 2
                        widthHole = widthHole / scaleFactor
                        heightHole = heightHole / scaleFactor

                        const newX = centerWidth - (centerWidth - stageRef.x()) / scaleFactor
                        const newY = centerHeight - (centerHeight - stageRef.y()) / scaleFactor
                        stageRef.position({ x: newX, y: newY })
                        stageRef.scale({ x: newScaleX, y: newScaleY })
                        stageRef.position({ x: newX, y: newY })
                        stageRef.scale({ x: newScaleX, y: newScaleY })
                        stageRef.batchDraw()
                    }

                    // mockups has only 1 side when combine layout
                    const calculatedRegion = {
                        space: 'viewport',
                        origin: 'center',
                        x: `48vw`,
                        y: '50vh',
                        w: widthHole * ratio + 20,
                        h: heightHole * ratio + 20,
                    }
                    return {
                        ...step,
                        canvasRegions: [calculatedRegion],
                    }
                }
            }
            return step
        })
        return adjustedSteps
    }, [mockupType, ratioDefault, stageRef])

    return (
        <div className="Step2Container newVersion" style={{overflow: 'hidden', maxHeight: '100vh'}}>
            {loading ? (
                <div className="LoadingWrapper">
                    <Spinner style={{ color: '#007BFF' }} />
                </div>
            ) : product ? (
                <Fragment>
                    {steps && stageRef && (
                        <Tour
                            steps={steps}
                            rectResolver={resolveCanvasFlexible}
                            beforeNext={({ _, step }) => {
                                if (step?.next) step.next(dispatch)
                            }}
                            open={openTour}
                            onClose={handleCloseTour}
                            onFinish={handleFinishTour}
                            productCreatedType={mockupType}
                        />
                    )}
                    <NavBar mappingProductId={mappingProductId} variantId={variantId} sku={sku} showDesignModeTooltip={showDesignModeTooltip}/>
                    <div
                        className={classNames(
                            'Wrapper position-relative',
                            { Design: display === 'design' },
                            { Pricing: display === 'pricing' },
                            { Design: display === 'design' },
                            { Pricing: display === 'pricing' },
                        )}
                        ref={containerRef}
                    >
                        <div id={'init_tour'} style={{ position: 'absolute', top: '50%', left: '50%', zIndex: 10 }}></div>
                        <div id={'init_tour'} style={{ position: 'absolute', top: '50%', left: '50%', zIndex: 10 }}></div>
                        {display === 'design' &&
                            ((mockupData?.meta?.version === 'v3' && mockupData?.preview_v3?.model_url) ||
                                (['v1', 'v2', 'v3', 'v2_2d', 'custom_shape'].includes(mockupData?.preview_type) &&
                                    mockupData?.meta?.use_client_mockup)) && (
                                <>
                                    <div
                                        id="left-sidebar"
                                        className="position-relative Left"
                                        style={{ width: `${!!leftWidth ? `${leftWidth}px` : 'revert-layer'}` }}
                                    >
                                        <PreviewLiveArtworkContainer />
                                    </div>
                                    <div className="separator" ref={separatorRef} onMouseDown={handleMouseDown}>
                                        <Menu />
                                    </div>
                                </>
                            )}

                        <div className="Center NewVersion">
                            <div className={className}>
                                <MockupEditor product={product} />
                            </div>
                            {display === 'preview' && <PreviewArtworkContainer display={display} />}
                            {['design', 'preview'].includes(display) && <MockupBottom />}
                            {display === 'pricing' && <ProductVariantPricingContainer display={display} />}
                        </div>

                        {display === 'design' && !tabDesign && (pending || runningImageCompress) && (
                            <div id="right-sidebar" className="position-absolute RightLoading">
                                <CampaignDesignLoading percentage={imageCompressProgress} />
                            </div>
                        )}

                        {(display !== 'design' || (display === 'design' && tabDesign)) && (
                            <div
                                id="right-sidebar"
                                className={`${display === 'design' ? 'position-absolute' : 'position-relative'} Right`}
                            >
                                <UploadContainer />
                                {tabDesign && display === 'design' && (
                                    <div className="iconCloseSidebar" onClick={onCloseSidebar} id="close_tab">
                                        <ArrowRight />
                                    </div>
                                )}
                            </div>
                        )}

                        {display === 'design' && (
                            <div id="toolbar" className="Toolbar">
                                <Toolbar />
                            </div>
                        )}
                    </div>
                </Fragment>
            ) : (
                <Fragment />
            )}
        </div >
    )
}

export default Step2Container

